import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MapPin, ArrowLeft, MagnifyingGlass, CurrencyDollar, Clock, Trash, ArrowRight, SpinnerGap } from '@phosphor-icons/react';
import { savedJobsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

const SavedJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchSavedJobs();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [page]);

  const fetchSavedJobs = async () => {
    try {
      setLoading(true);
      const response = await savedJobsAPI.getSavedJobs({ page, limit: 10 });
      if (response.data.success) {
        setJobs(response.data.jobs);
        setTotalPages(response.data.pages);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch saved jobs:', error);
      toast({
        title: "Error",
        description: "Failed to load saved jobs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnsaveJob = async (e, jobId) => {
    e.stopPropagation();
    try {
      await savedJobsAPI.unsaveJob(jobId);
      setJobs(jobs.filter(job => job._id !== jobId));
      setTotal(prev => prev - 1);
      toast({
        title: "Removed",
        description: "Job removed from saved list"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove job",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="saved-jobs-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white font-bold mb-4"
          >
            <ArrowLeft size={24} weight="bold" />
            Back
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
              <Heart size={36} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black">Saved Jobs</h1>
              <p className="text-blue-100 text-lg">
                {total} job{total !== 1 ? 's' : ''} saved
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border-2 border-black rounded-xl p-8 shadow-[4px_4px_0px_#000] text-center">
            <Heart size={80} weight="light" className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-gray-900 mb-2">No saved jobs yet</h2>
            <p className="text-gray-600 mb-6 text-lg">
              Browse jobs and tap the heart to save ones you like
            </p>
            <button 
              onClick={() => navigate('/browse-jobs')}
              className="h-16 px-8 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            >
              <MagnifyingGlass size={24} weight="bold" className="inline mr-2" />
              Find Jobs
            </button>
          </div>
        ) : (
          <>
            {/* Job Cards - Simplified */}
            <div className="space-y-4">
              {jobs.map((job) => (
                <div 
                  key={job._id}
                  className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] transition-all cursor-pointer"
                  onClick={() => navigate(`/job/${job._id}`)}
                  data-testid={`saved-job-card-${job._id}`}
                >
                  {/* Pay Rate - Most Important */}
                  <div className="flex items-center gap-2 mb-2">
                    <CurrencyDollar size={28} weight="bold" className="text-green-600" />
                    <span className="text-2xl font-black text-green-600">
                      {job.budget || 'Negotiable'}
                    </span>
                    <span className={`ml-auto px-3 py-1 rounded-full text-sm font-bold border-2 ${
                      job.status === 'open' 
                        ? 'bg-green-100 text-green-700 border-green-400' 
                        : 'bg-gray-100 text-gray-600 border-gray-300'
                    }`}>
                      {job.status === 'open' ? 'Hiring' : job.status}
                    </span>
                  </div>

                  {/* Job Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {job.title}
                  </h3>

                  {/* Quick Info */}
                  <div className="flex flex-wrap gap-4 mb-4 text-gray-700">
                    {job.location && (
                      <div className="flex items-center gap-1">
                        <MapPin size={20} weight="bold" />
                        <span className="font-medium">{job.location}</span>
                      </div>
                    )}
                    {job.duration && (
                      <div className="flex items-center gap-1">
                        <Clock size={20} weight="bold" />
                        <span className="font-medium">{job.duration}</span>
                      </div>
                    )}
                  </div>

                  {/* Skills */}
                  {job.required_skills && job.required_skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {job.required_skills.slice(0, 3).map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-gray-100 border-2 border-gray-300 rounded-full text-sm font-bold text-gray-700"
                        >
                          {skill}
                        </span>
                      ))}
                      {job.required_skills.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 border-2 border-gray-300 rounded-full text-sm font-bold text-gray-500">
                          +{job.required_skills.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Saved Date */}
                  <p className="text-sm text-gray-500 mb-4">
                    Saved {formatDate(job.saved_at)}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/job/${job._id}`);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 h-14 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                    >
                      View Job
                      <ArrowRight size={24} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => handleUnsaveJob(e, job._id)}
                      className="h-14 w-14 flex items-center justify-center bg-red-50 text-red-600 rounded-xl border-2 border-red-300 hover:bg-red-100 transition-all"
                      data-testid={`unsave-job-${job._id}`}
                    >
                      <Trash size={24} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-12 px-6 bg-white text-gray-700 font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] disabled:opacity-50 disabled:shadow-none transition-all"
                >
                  Previous
                </button>
                <span className="text-gray-700 font-bold">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-12 px-6 bg-white text-gray-700 font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] disabled:opacity-50 disabled:shadow-none transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SavedJobs;
